import {useCallback, useEffect, useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import {SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";

const SURVEY_ID_QUERY_PARAM = "surveyId";

interface SelectableSurvey {
    id: string;
    type?: SurveyType;
}

export interface SelectedSurveyState {
    selectedSurveyId?: string;
    selectSurvey: (surveyId: string) => void;
}

export function useSelectedSurveyId(surveys: readonly SelectableSurvey[]): SelectedSurveyState {
    const [searchParams, setSearchParams] = useSearchParams();
    const organization = useOrganization();
    const storageKey = `surveySelectedSurveyId-${organization?.id}`;
    const surveyIdFromUrl = searchParams.get(SURVEY_ID_QUERY_PARAM);

    const selectedSurveyId = useMemo(() => {
        const knownId = (surveyId: string | null): surveyId is string => (
            !!surveyId && surveys.some(survey => survey.id === surveyId)
        );

        if (knownId(surveyIdFromUrl)) return surveyIdFromUrl;

        const sessionSurveyId = sessionStorage.getItem(storageKey);
        if (knownId(sessionSurveyId)) return sessionSurveyId;

        const persistedSurveyId = localStorage.getItem(storageKey);
        if (knownId(persistedSurveyId)) return persistedSurveyId;

        const guestSurvey = surveys.find(survey => survey.type !== SurveyType.ANONYMOUS);
        return (guestSurvey ?? surveys[0])?.id;
    }, [storageKey, surveyIdFromUrl, surveys]);

    useEffect(() => {
        if (!selectedSurveyId) return;

        sessionStorage.setItem(storageKey, selectedSurveyId);
        localStorage.setItem(storageKey, selectedSurveyId);
        if (surveyIdFromUrl !== selectedSurveyId) {
            setSearchParams(currentSearchParams => {
                const nextSearchParams = new URLSearchParams(currentSearchParams);
                nextSearchParams.set(SURVEY_ID_QUERY_PARAM, selectedSurveyId);
                return nextSearchParams;
            }, {replace: true});
        }
    }, [selectedSurveyId, setSearchParams, storageKey, surveyIdFromUrl]);

    const selectSurvey = useCallback((surveyId: string) => {
        if (!surveys.some(survey => survey.id === surveyId)) return;

        sessionStorage.setItem(storageKey, surveyId);
        localStorage.setItem(storageKey, surveyId);
        setSearchParams(currentSearchParams => {
            const nextSearchParams = new URLSearchParams(currentSearchParams);
            nextSearchParams.set(SURVEY_ID_QUERY_PARAM, surveyId);
            return nextSearchParams;
        }, {replace: true});
    }, [setSearchParams, storageKey, surveys]);

    return {selectedSurveyId, selectSurvey};
}
